import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandMark";
import { Loader2 } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthModal() {
  const { showAuth, setShowAuth, authMode, setAuthMode, login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const isRegister = authMode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isRegister) {
        await register(email, password, name);
        toast.success("Account created — your clips are unlocked!");
      } else {
        await login(email, password);
        toast.success("Welcome back!");
      }
      setShowAuth(false);
      setEmail("");
      setPassword("");
      setName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={showAuth} onOpenChange={setShowAuth}>
      <DialogContent className="bg-surface-950 border-surface-800 text-foreground sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <BrandMark testId="brand-mark-auth" className="h-9 w-9 shrink-0" />
            <DialogTitle className="font-display text-2xl font-bold">
              {isRegister ? "Create your account" : "Welcome back"}
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-copy-400">
            {isRegister ? "Unlock unlimited watermark-free clips." : "Log in to access your dashboard."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {isRegister && (
            <div className="space-y-1.5">
              <Label className="text-copy-300">Name</Label>
              <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Alex Creator" className="bg-surface-900 border-surface-800 focus-visible:ring-cyan-500" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-copy-300">Email</Label>
            <Input data-testid="auth-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com"
              className="bg-surface-900 border-surface-800 focus-visible:ring-cyan-500" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-copy-300">Password</Label>
            <Input data-testid="auth-password-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className="bg-surface-900 border-surface-800 focus-visible:ring-cyan-500" />
          </div>
          <Button data-testid="auth-submit-btn" type="submit" disabled={busy}
            className="w-full rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold h-11">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isRegister ? "Create account" : "Log in"}
          </Button>
        </form>
        <div className="text-center text-sm text-copy-500">
          {isRegister ? "Already have an account?" : "New here?"}{" "}
          <button data-testid="auth-toggle-btn" onClick={() => setAuthMode(isRegister ? "login" : "register")}
            className="text-cyan-400 hover:text-cyan-300 font-medium">
            {isRegister ? "Log in" : "Create one"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
